import type { ElementType } from 'react';
export type WidgetType = 'keyMetric' | 'timeSeries' | 'topList' | 'pieChart' | 'table';
export type WidgetDomain =
    | 'order'
    | 'location'
    | 'vendor'
    | 'insurance'
    | 'oem'
    | 'partType'
    // Invoicing dashboard domains
    | 'invoicingTotal'
    | 'invoicingLocation'
    | 'invoicingVendor'
    // Inventory dashboard domains
    | 'inventoryTotal'
    | 'inventoryBusinessLine'
    | 'inventoryCategory'
    | 'inventoryLocation';
export type WidgetStat = 'total' | 'average' | 'median';
export type WidgetFormat = 'currency' | 'percent' | 'number';
export type WidgetSort = 'asc' | 'desc';
export type WidgetTimeInterval = 'day' | 'week' | 'month';
export type WidgetLayout = 'vertical' | 'horizontal';

export interface WidgetConfig {
    id: string;
    title: string;
    description?: string;
    widgetType: WidgetType;
    domain: WidgetDomain;
    metric: string;
    stat: WidgetStat;
    format: WidgetFormat;
    layout?: WidgetLayout;
    timeInterval?: WidgetTimeInterval;
    sort?: WidgetSort;
    cutoff?: number;
    comparison?: boolean;
    gridSpan?: number;
    widgetHeight?: number;
    gridX?: number;
    gridY?: number;
    dataSource: 'summary' | 'timeseries' | 'rankings';
    dataKey?: string;
    showComparisonBars?: boolean;
    showAllRows?: boolean;
    /** Segment the Total Orders bar chart by marketplace (sourceData.source). */
    showMarketplaceBreakdown?: boolean;
    /** Which dashboard this widget belongs to. Defaults to 'overview' when unset. */
    dashboard?: 'overview' | 'invoicing' | 'inventory';
    /** Invoicing time-series: stack the bars by invoice type (Received / Credit / Return Slip / Sublet). */
    showInvoiceTypeBreakdown?: boolean;
    /** Chart presentation style (invoicing / inventory dashboards). */
    chartStyle?: ChartStyle;
    excludeEvs?: boolean;
    /**
     * Inventory In-Stock Over Time: when true, scope the series to restockAlert
     * items and show Target / Reorder dashed reference lines.
     */
    restockItemsOnly?: boolean;
}

/** Chart presentation styles offered as widget-type cards on non-overview dashboards. */
export type ChartStyle = 'bars' | 'stacked' | 'line' | 'donut' | 'histogram';

// ============================================================================
// EDITOR CATALOG (parameterizes WidgetEditDrawer for non-overview dashboards)
// ============================================================================

export interface WidgetEditorVariantGroup {
    label: string;
    variants: { value: string; label: string; }[];
}

/**
 * Describes the domains, metrics, widget types and toggles a dashboard exposes
 * in the shared WidgetEditDrawer. When omitted the drawer uses the built-in
 * Overview catalog (unchanged behavior).
 */
export interface WidgetEditorCatalog {
    /** "Widget type" cards. `value` is a card id (e.g. 'keyMetric', 'bars', 'stacked', 'line', 'donut', 'histogram' [Bar Chart label]). */
    widgetTypes: { value: string; label: string; icon: ElementType; }[];
    /** When set, filters widget-type cards for the current draft (e.g. hide donut for signed-$ metrics). */
    widgetTypesFor?: (config: WidgetConfig) => { value: string; label: string; icon: ElementType; }[];
    domains: { value: WidgetDomain; label: string; }[];
    /** When set, filters domain options for the current draft (e.g. hide location for time series bar). */
    domainsFor?: (config: WidgetConfig) => { value: WidgetDomain; label: string; }[];
    /** Metrics available for the current draft. */
    metricsFor: (config: WidgetConfig) => MetricCatalogEntry[];
    /** Avg/Median (or unit) variant groups to condense in the metric dropdown. */
    variantGroups?: WidgetEditorVariantGroup[];
    /** The card id currently selected for the given config. */
    selectedCardId: (config: WidgetConfig) => string;
    /** Partial config to apply when a card is chosen. */
    applyCardId: (cardId: string, config: WidgetConfig) => Partial<WidgetConfig>;
    /** Partial config to apply when the domain changes. */
    applyDomain?: (domain: WidgetDomain, config: WidgetConfig) => Partial<WidgetConfig>;
    /** Partial config to apply when the metric changes (e.g. fall back from an invalid chart style). */
    applyMetric?: (metric: string, config: WidgetConfig) => Partial<WidgetConfig>;
    /** Whether to show the "Show prior period" toggle for the current draft. */
    showPriorFor?: (config: WidgetConfig) => boolean;
    /** Whether to show default sort order controls for the current draft. */
    showSortFor?: (config: WidgetConfig) => boolean;
    /** Whether to show location count limit controls (line chart by location). */
    showLocationLimitFor?: (config: WidgetConfig) => boolean;
    /** Inventory In-Stock: show "Restock Items Only" configuration toggle. */
    showRestockItemsOnlyFor?: (config: WidgetConfig) => boolean;
    defaultConfig: () => WidgetConfig;
}

export interface MetricCatalogEntry {
    value: string;
    label: string;
    format: WidgetFormat;
    tooltip?: string;
    stat: WidgetStat;
}

export const SUMMARY_METRICS: MetricCatalogEntry[] = [
    { value: 'totalOrders', label: 'Total Orders', format: 'number', stat: 'total', tooltip: 'Total number of orders' },
    { value: 'totalVolume', label: 'Total Volume', format: 'currency', stat: 'total', tooltip: 'Total dollars of parts ordered' },
    { value: 'totalOrderCost', label: 'Total Cost', format: 'currency', stat: 'total', tooltip: 'Total dollars paid to suppliers for parts ordered' },
    { value: 'totalVolumeWoPM', label: 'Total Volume (w/o PM)', format: 'currency', stat: 'total', tooltip: 'Total list dollars using base listPrice instead of adjustedListPrice from price matching' },
    { value: 'avgGrossMargin', label: 'Avg. Gross Margin', format: 'percent', stat: 'average', tooltip: 'Total list minus total buy, divided by total list' },
    { value: 'avgGrossMarginWoPM', label: 'Avg. Gross Margin (w/o PM)', format: 'percent', stat: 'average', tooltip: 'Same dollar-weighting as Average Gross Margin but using base listPrice' },
    { value: 'totalParts', label: 'Total Parts', format: 'number', stat: 'total', tooltip: 'Total number of parts ordered' },
    { value: 'grossProfit', label: 'Gross Profit', format: 'currency', stat: 'total', tooltip: 'Sum of all orders adjusted list price minus the buy price' },
    { value: 'avgListPrice', label: 'Avg. List Price', format: 'currency', stat: 'average', tooltip: 'Total volume divided by total parts ordered' },
    { value: 'medianListPrice', label: 'Median List Price', format: 'currency', stat: 'median', tooltip: 'Median of list prices of parts ordered' },
    { value: 'avgOrderSize', label: 'Avg. Order Size', format: 'currency', stat: 'average', tooltip: 'Total volume divided by total orders' },
    { value: 'medianOrderSize', label: 'Median Order Size', format: 'currency', stat: 'median', tooltip: 'Median of each order total dollars' },
    { value: 'medianGrossMargin', label: 'Median Gross Margin', format: 'percent', stat: 'median', tooltip: 'Median of the per-order gross margins' },
    { value: 'medianGrossMarginWoPM', label: 'Median Gross Margin (w/o PM)', format: 'percent', stat: 'median', tooltip: 'Median of per-order gross margins using base listPrice' },
];

export const TIMESERIES_VOLUME_METRICS: MetricCatalogEntry[] = [
    { value: 'orderVolume', label: 'Order Volume', format: 'currency', stat: 'total', tooltip: 'Total dollars of parts ordered' },
    { value: 'orderVolumeWoPM', label: 'Order Volume (w/o PM)', format: 'currency', stat: 'total', tooltip: 'Total list dollars using base listPrice instead of adjustedListPrice from price matching' },
    { value: 'orderCost', label: 'Order Cost', format: 'currency', stat: 'total', tooltip: 'Total dollars paid to suppliers for parts ordered' },
    { value: 'orderCount', label: 'Order Count', format: 'number', stat: 'total', tooltip: 'Total number of orders' },
    { value: 'avgOrderSize', label: 'Avg. Order Size', format: 'currency', stat: 'average', tooltip: 'Total dollars divided by total parts' },
    { value: 'medianOrderSize', label: 'Median Order Size', format: 'currency', stat: 'median', tooltip: 'Median of each order total dollars' },
    { value: 'grossProfit', label: 'Gross Profit', format: 'currency', stat: 'total', tooltip: 'Sum of adjusted list price minus buy price' },
    { value: 'partsOrdered', label: 'Parts Ordered', format: 'number', stat: 'total', tooltip: 'Total number of parts ordered' },
    { value: 'avgListPrice', label: 'Avg. List Price', format: 'currency', stat: 'average', tooltip: 'Total volume divided by parts ordered' },
    { value: 'medianListPrice', label: 'Median List Price', format: 'currency', stat: 'median', tooltip: 'Median of list prices of parts ordered' },
    { value: 'avgListPriceWoPM', label: 'Avg. List Price (w/o PM)', format: 'currency', stat: 'average', tooltip: 'Average list price using the base selected list price before price matching adjustments' },
    { value: 'medianListPriceWoPM', label: 'Median List Price (w/o PM)', format: 'currency', stat: 'median', tooltip: 'Median list price using the base selected list price before price matching adjustments' },
    { value: 'pctOemVolume', label: '% OEM (Volume)', format: 'percent', stat: 'average', tooltip: 'Percentage of order volume from OEM parts' },
    { value: 'pctOemCount', label: '% OEM (Count)', format: 'percent', stat: 'average', tooltip: 'Percentage of order count from OEM parts' },
    { value: 'pctLkqVolume', label: '% LKQ (Volume)', format: 'percent', stat: 'average', tooltip: 'Percentage of order volume from LKQ parts' },
    { value: 'pctLkqCount', label: '% LKQ (Count)', format: 'percent', stat: 'average', tooltip: 'Percentage of order count from LKQ parts' },
    { value: 'pctAftermarketVolume', label: '% Aftermarket (Volume)', format: 'percent', stat: 'average', tooltip: 'Percentage of order volume from aftermarket parts' },
    { value: 'pctAftermarketCount', label: '% Aftermarket (Count)', format: 'percent', stat: 'average', tooltip: 'Percentage of order count from aftermarket parts' },
];

export const TIMESERIES_MARGIN_METRICS: MetricCatalogEntry[] = [
    { value: 'avgGrossMarginPct', label: 'Avg. Gross Margin', format: 'percent', stat: 'average', tooltip: 'Adjusted list minus buy divided by adjusted list' },
    { value: 'medianGrossMarginPct', label: 'Median Gross Margin', format: 'percent', stat: 'median', tooltip: 'Median of the per-order gross margins' },
    { value: 'avgGrossMarginPctWoPM', label: 'Avg. Gross Margin (w/o PM)', format: 'percent', stat: 'average', tooltip: 'Gross margins without price matching adjustments' },
    { value: 'medianGrossMarginPctWoPM', label: 'Median Gross Margin (w/o PM)', format: 'percent', stat: 'median', tooltip: 'Median of the per-order gross margins without price matching adjustments' },
    { value: 'grossMarginLossFromPM', label: 'Gross Margin Lost from PM', format: 'percent', stat: 'average', tooltip: 'Difference in GM with and without price matching' },
    { value: 'partListPricePctOfOE', label: 'Part List Price % of OE', format: 'percent', stat: 'average', tooltip: 'Adjusted list price divided by original list price' },
    { value: 'missedOpportunity', label: 'Missed Opportunity', format: 'currency', stat: 'total', tooltip: 'List price minus insurance best for all parts' },
    { value: 'avgMissedOpportunity', label: 'Avg. Missed Opportunity', format: 'currency', stat: 'average', tooltip: 'Total missed opportunity divided by orders with at least one eligible part' },
    { value: 'medianMissedOpportunity', label: 'Median Missed Opportunity', format: 'currency', stat: 'median', tooltip: 'Median of the per-order missed opportunities, including eligible orders with $0' },
];

export const RANKING_METRICS: MetricCatalogEntry[] = [
    { value: 'orderVolume', label: 'Order Volume', format: 'currency', stat: 'total', tooltip: 'Total dollars of parts ordered' },
    { value: 'orderCost', label: 'Order Cost', format: 'currency', stat: 'total', tooltip: 'Total dollars paid to suppliers for parts ordered' },
    { value: 'orderCount', label: 'Order Count', format: 'number', stat: 'total', tooltip: 'Total number of orders' },
    { value: 'avgOrderSize', label: 'Avg. Order Size', format: 'currency', stat: 'average', tooltip: 'Total dollars divided by total orders' },
    { value: 'medianOrderSize', label: 'Median Order Size', format: 'currency', stat: 'median', tooltip: 'Median of each order total dollars' },
    { value: 'grossProfit', label: 'Gross Profit', format: 'currency', stat: 'total', tooltip: 'Adjusted list minus buy price' },
    { value: 'avgGrossMarginPct', label: 'Avg. Gross Margin', format: 'percent', stat: 'average', tooltip: 'Adjusted list minus buy divided by adjusted list' },
    { value: 'medianGrossMarginPct', label: 'Median Gross Margin', format: 'percent', stat: 'median', tooltip: 'Median of the per-order gross margins' },
    { value: 'avgGrossMarginPctWoPM', label: 'Avg. Gross Margin (w/o PM)', format: 'percent', stat: 'average', tooltip: 'Gross margins without price matching adjustments' },
    { value: 'medianGrossMarginPctWoPM', label: 'Median Gross Margin (w/o PM)', format: 'percent', stat: 'median', tooltip: 'Median of the per-order gross margins without PM' },
    { value: 'grossMarginLossFromPM', label: 'Gross Margin Lost from PM', format: 'percent', stat: 'average', tooltip: 'Difference in GM with/without PM' },
    { value: 'partListPricePctOfOE', label: 'Part List Price % of OE', format: 'percent', stat: 'average', tooltip: 'Adjusted list price divided by original list price' },
    { value: 'missedOpportunity', label: 'Missed Opportunity', format: 'currency', stat: 'total', tooltip: 'List price minus insurance best for all parts' },
    { value: 'avgMissedOpportunity', label: 'Avg. Missed Opportunity', format: 'currency', stat: 'average', tooltip: 'Total missed opportunity divided by orders with at least one eligible part' },
    { value: 'medianMissedOpportunity', label: 'Median Missed Opportunity', format: 'currency', stat: 'median', tooltip: 'Median of the per-order missed opportunities, including eligible orders with $0' },
    { value: 'pctOfTotalVolume', label: '% of Total Volume', format: 'percent', stat: 'total', tooltip: 'Percentage of total order volume' },
    { value: 'pctOfTotalCount', label: '% of Total Orders', format: 'percent', stat: 'total', tooltip: 'Percentage of total order count' },
    { value: 'pctOfTotalProfit', label: '% of Total Profit', format: 'percent', stat: 'total', tooltip: 'Percentage of total gross profit' },
];

export const STANDARD_CHART_HEIGHT = 340;
export const WIDGET_GRID_GAP_PX = 16;
export const WIDGET_SPAN_STEPS = 4;
export const WIDGET_HEIGHT_STEPS = 3;

export function clampGridSpan(span?: number): number {
    if (span == null || !Number.isFinite(span)) return 2;
    return Math.min(WIDGET_SPAN_STEPS, Math.max(1, Math.round(span)));
}

/** Missing height (saved boards before this field) is one row — today's 340px card. */
export function clampWidgetHeight(height?: number): number {
    if (height == null || !Number.isFinite(height)) return 1;
    return Math.min(WIDGET_HEIGHT_STEPS, Math.max(1, Math.round(height)));
}

/** Remount Recharts when the card's grid cell changes so it remeasures at 100%. */
export function widgetChartLayoutKey(config: Pick<WidgetConfig, 'gridSpan' | 'widgetHeight'>): string {
    return `${clampGridSpan(config.gridSpan)}x${clampWidgetHeight(config.widgetHeight)}`;
}

export interface WidgetGridSize {
    gridSpan: number;
    widgetHeight: number;
    gridX?: number;
    gridY?: number;
}

export type ResizeCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

// ============================================================================
// MARKETPLACE BREAKDOWN
// ============================================================================

/**
 * Metrics that are additive totals across marketplaces — rendered as a
 * stacked bar chart (height of each segment sums to the period total).
 */
export const MARKETPLACE_STACKED_METRICS = new Set<string>([
    'orderVolume',
    'orderVolumeWoPM',
    'orderCost',
    'orderCount',
    'grossProfit',
]);

/**
 * Metrics that should never be split by marketplace. Missed opportunity and
 * part list price % of OE aren't meaningfully attributable per marketplace; and
 * the OEM "Suggested Thresholds" (p90 gross margin) is excluded for OEM.
 */
export const MARKETPLACE_EXCLUDED_METRICS = new Set<string>([
    'missedOpportunity',
    'avgMissedOpportunity',
    'medianMissedOpportunity',
    'partListPricePctOfOE',
    // OEM suggested thresholds
    'p90GrossMarginPct',
]);

/** Domains that can be split by marketplace (Insurance & Part Type are excluded). */
export const MARKETPLACE_BREAKDOWN_DOMAINS = new Set<WidgetDomain>([
    'order',
    'location',
    'vendor',
    'oem',
]);

/**
 * Whether a widget supports a marketplace breakdown. Order domain supports the
 * time-series bar; the entity domains (location/vendor/oem) support both the
 * clustered ranking bar chart and the expandable table.
 */
export function supportsMarketplaceBreakdown(domain: WidgetDomain, widgetType: WidgetType, metric: string): boolean {
    if (!MARKETPLACE_BREAKDOWN_DOMAINS.has(domain)) return false;
    if (MARKETPLACE_EXCLUDED_METRICS.has(metric)) return false;
    if (domain === 'order') return widgetType === 'timeSeries';
    return widgetType === 'timeSeries' || widgetType === 'topList' || widgetType === 'table';
}

/** Stacked when the metric is additive across marketplaces, clustered otherwise. */
export function isStackedMarketplaceMetric(metric: string): boolean {
    return MARKETPLACE_STACKED_METRICS.has(metric);
}

export const GRID_SPAN_LABELS: Record<number, string> = {
    1: 'Small',
    2: 'Half',
    3: 'Large',
    4: 'Full',
};

export const WIDGET_TYPE_OPTIONS: { value: WidgetType; label: string; icon: string; }[] = [
    { value: 'keyMetric', label: 'Key Metric', icon: '$' },
    { value: 'timeSeries', label: 'Time Series', icon: '📈' },
    { value: 'topList', label: 'Top List', icon: '≡' },
    { value: 'table', label: 'Table', icon: '⊞' },
];

export const DOMAIN_OPTIONS: { value: WidgetDomain; label: string; }[] = [
    { value: 'order', label: 'Total Orders' },
    { value: 'location', label: 'Location' },
    { value: 'vendor', label: 'Vendor' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'oem', label: 'OEM' },
    { value: 'partType', label: 'Part Type' },
];
